package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.cssRem

/**
 * Email sign-in dialog. [onSend] returns null on success or a backend error code
 * (INVALID_EMAIL, RATE_LIMIT_EXCEEDED, ...), which is shown as a friendly message.
 */
@Composable
fun LoginDialog(
    visible: Boolean,
    onDismiss: () -> Unit,
    onSend: suspend (email: String) -> String?,
    launch: (suspend () -> Unit) -> Unit,
    legalLinks: (@Composable () -> Unit)? = null
) {
    if (!visible) return
    val s = strings().auth
    val notApproved = strings().access.notApproved
    val palette = LocalAppTheme.current.colors
    var email by remember { mutableStateOf("") }
    var sending by remember { mutableStateOf(false) }
    var sentTo by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf<String?>(null) }

    fun submit() {
        val value = email.trim()
        if (value.isEmpty() || sending) return
        sending = true
        error = null
        launch {
            val code = onSend(value)
            sending = false
            when (code) {
                null -> sentTo = value
                "INVALID_EMAIL" -> error = s.invalidEmail
                "RATE_LIMIT_EXCEEDED" -> error = s.throttled
                "ACCESS_NOT_APPROVED" -> error = notApproved
                else -> error = s.sendFailed
            }
        }
    }

    val sent = sentTo
    ModalDialog(
        visible = true,
        onCloseRequest = onDismiss,
        title = if (sent == null) s.dialogTitle else s.sentTitle,
        footer = if (sent == null) {
            ModalDialogConfirmActions(
                confirmText = s.sendLink,
                onConfirm = { submit() },
                onCancel = onDismiss,
                confirmLoading = sending,
                confirmEnabled = email.isNotBlank() && !sending
            )
        } else {
            {
                AppButton(text = s.useAnotherEmail, onClick = { sentTo = null }, variant = AppButtonVariant.OUTLINE)
            }
        }
    ) {
        Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
            if (sent == null) {
                SpanText(s.dialogHint, Modifier.fontSize(0.85.cssRem).color(palette.textSecondary))
                AppInput(
                    value = email,
                    onValueChanged = { email = it; error = null },
                    label = s.emailLabel,
                    placeholder = s.emailPlaceholder,
                    type = InputType.Email,
                    error = error,
                    disabled = sending
                )
                SpanText(strings().legal.loginConsent, Modifier.fontSize(0.8.cssRem).color(palette.textSecondary))
                legalLinks?.invoke()
            } else {
                SpanText(s.sentHint(sent), Modifier.fontSize(0.9.cssRem).color(palette.textPrimary))
            }
        }
    }
}
